"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Briefcase, Building2, Users, Plus, X } from "lucide-react";
import { getWilayas } from "../../apis/location";
import { getStats } from "../../apis/public";
import { useEffect } from "react";

const JobSearchLanding = () => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [wilayas, setWilayas] = useState([]);
  const [location, setLocation] = useState("");

  const [isSearching, setIsSearching] = useState(false);
  const [statsData, setStatsData] = useState({
    jobsCount: 0,
    companiesCount: 0,
    candidatesCount: 0,
    newJobsCount: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getStats();
        setStatsData(data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  // ... keep your fetchWilayas logic ...
  const fetchWilayas = async (keyword) => {
    setLocation(keyword);
    if (keyword.length > 0) {
      try {
        const res = await getWilayas(keyword);
        setWilayas(res);
      } catch (error) {
        console.error("Error fetching wilayas:", error);
      }
    } else {
      setWilayas([]);
    }
  };

  const selectWilaya = (wilayaName) => {
    setLocation(wilayaName);
    setWilayas([]);
  };

  const clearWilaya = () => {
    setLocation("");
    setWilayas([]);
  };

  const handleSearch = () => {
    setIsSearching(true);
    const params = new URLSearchParams();
    if (searchTerm) params.append("keyword", searchTerm);
    if (location) params.append("location", location);

    navigate(`/job-offers?${params.toString()}`);
    setIsSearching(false);
  };

  const stats = [
    { icon: Briefcase, number: statsData.jobsCount.toLocaleString(), label: t("landing.stats.liveJobs") },
    { icon: Building2, number: statsData.companiesCount.toLocaleString(), label: t("landing.stats.companies") },
    { icon: Users, number: statsData.candidatesCount.toLocaleString(), label: t("landing.stats.candidates") },
    { icon: Plus, number: statsData.newJobsCount.toLocaleString(), label: t("landing.stats.newJobs") },
  ];

  return (
    <div className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
      {/* Background Blobs - Unified Style */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full z-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-yellow-400/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-4 mb-10"
        >
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 dark:text-white tracking-tight leading-tight">
            {t('landing.title1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600">{t('landing.title2')}</span>
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            {t("landing.subtitle")}
          </p>
        </motion.div>

        {/* Search Bar - Glassmorphism Pill */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="max-w-4xl mx-auto relative z-20"
        >
          <div className="p-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-3xl shadow-2xl flex flex-col md:flex-row items-center gap-2">

            {/* Keyword Input */}
            <div className="relative flex-1 w-full group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-yellow-500 transition-colors" size={20} />
              <input
                type="text"
                placeholder={t("landing.searchInputPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400"
              />
              <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-8 bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Location Input */}
            <div className="relative flex-1 w-full group">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-yellow-500 transition-colors" size={20} />
              <input
                type="text"
                placeholder={t("landing.wilayaPlaceholder")}
                value={location}
                onChange={(e) => fetchWilayas(e.target.value)}
                className="w-full pl-12 pr-10 py-4 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400"
              />
              {location && (
                <button onClick={clearWilaya} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
              {/* Dropdown Logic Here (Keep your existing dropdown UI but styled) */}
              {wilayas.length > 0 && (
                <div className="absolute top-full left-0 w-full mt-2 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                  {wilayas.map((w) => (
                    <div key={w.code} onClick={() => selectWilaya(w.name_fr)} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer text-left text-sm text-gray-700 dark:text-gray-300">
                      {w.name_fr}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="w-full md:w-auto px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-lg shadow-gray-900/10"
            >
              {isSearching ? t("landing.searching") : t("landing.searchButton")}
            </button>
          </div>
        </motion.div>


        {/* Stats Grid */}
        {/* <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20"
        >
          {stats.map((stat, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-white/50 dark:border-gray-700/50 hover:bg-white dark:hover:bg-gray-800 transition-colors group">
              <div className="flex flex-col items-center gap-2">
                <div className="p-3 rounded-full bg-primary-600/5 dark:bg-primary-200/5 text-primary-600 dark:text-primary-100 group-hover:scale-110 transition-transform">
                  <stat.icon size={24} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{stat.number}</h3>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div> */}
      </div>
    </div>
  );
};

export default JobSearchLanding;